import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { RoomQR } from '../../../components/RoomQR'
import { RankMedal } from '../../../components/RankMedal'
import { useAuth } from '../../../lib/auth'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import { COLORS_BY_ID, COLOR_WORDS, type ColorId } from '../data/colors'
import { ColorCardImage, CategoryCardImage } from '../components/ColorCardImage'
import { AllRoundsRecap, type RecapRound } from '../components/AllRoundsRecap'
import { CATEGORIES } from '../data/categories'
import { requestAiColorPick } from './../api/aiColorPick'
import { requestAiSubject } from '../api/aiSubject'
import { fetchColorfulAiPlayers } from '../api/aiPlayers'
import {
  addColorfulAi, beginColorfulRound, confirmColorfulCard,
  drawColorfulCard, fetchColorfulPicks, fetchColorfulPlayers, fetchColorfulRoomById,
  kickColorfulPlayer,
  leaveColorfulRoom, redrawColorfulCard, removeColorfulPlayer, renameColorfulSeat,
  revealColorfulRound, setColorfulRevealIndex, startColorful, submitColorfulPick, submitColorfulPickAsAi,
} from '../api/rooms'
import type {
  AiPlayerRow, ColorfulPickRow, ColorfulPlayerRow, ColorfulRoomRow,
} from '../../../lib/database.types'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface Props {
  roomId: string
  onExit: () => void
}

/**
 * The one Colorful room view. Reads `colorful_rooms.phase` from realtime
 * and renders a different sub-view per phase. Host has extra controls.
 *
 * This deliberately lives in the main app (phones). TV mode — where the
 * browser is a pure big-screen display and phones are controllers —
 * reuses the same state machine but swaps the render layer; shipping
 * that is a follow-up.
 */
export function ColorfulRoomScreen({ roomId, onExit }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const me = profile?.id ?? null

  const [room, setRoom]       = useState<ColorfulRoomRow | null>(null)
  const [players, setPlayers] = useState<ColorfulPlayerRow[]>([])
  const [picks, setPicks]     = useState<ColorfulPickRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [aiBusy, setAiBusy]   = useState(false)

  const isHost = Boolean(room && me && room.host_id === me)
  const mySeat = players.find(p => p.profile_id === me) ?? null
  // Any present (non-AI, non-skipped) human drives the AI / auto-effects,
  // so the game never depends on the host staying connected. The server
  // RPCs are idempotent + phase-guarded, so several humans driving at
  // once is harmless (duplicate calls no-op).
  const iDrive = Boolean(mySeat && !mySeat.is_ai && !mySeat.skipped)

  // ── Initial fetch + realtime subscriptions ────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [r, ps, pk] = await Promise.all([
          fetchColorfulRoomById(roomId),
          fetchColorfulPlayers(roomId),
          fetchColorfulPicks(roomId),
        ])
        if (cancelled) return
        if (!r) throw new Error('Room not found')
        setRoom(r); setPlayers(ps); setPicks(pk)
        // A successful (re)load clears any stale transient error — the
        // 5s poll makes recovery automatic instead of a dead-end screen.
        setError(null)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()

    const channel = supabase.channel(`colorful-room-${roomId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_rooms', filter: `id=eq.${roomId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return
          setRoom(payload.new as ColorfulRoomRow)
        })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_players', filter: `room_id=eq.${roomId}` },
        () => { fetchColorfulPlayers(roomId).then(setPlayers).catch(() => {}) })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_picks', filter: `room_id=eq.${roomId}` },
        () => { fetchColorfulPicks(roomId).then(setPicks).catch(() => {}) })
      .subscribe()

    // Polling + resume fallback (same rationale as TokyoMetroRoomScreen):
    // iOS suspends the JS runtime when the app backgrounds, the websocket
    // dies, and missed realtime events are never replayed. Colorful is
    // the worst case — a suspended picker used to deadlock the whole
    // table. The 5s poll + visibility refetch keep everyone converging.
    const poll = setInterval(load, 5000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      supabase.removeChannel(channel)
      clearInterval(poll)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [roomId])

  // ── Auto-draw at top level ────────────────────────────────────────────
  // The moment any client (the host) sees `phase === 'round_intro'`,
  // fire `drawColorfulCard` so we collapse round_intro + picker_draw
  // into a single "Your Pick" screen for everyone. Reset the ref when
  // we leave round_intro so the next round can fire again.
  const hasFiredDrawRef = useRef(false)
  useEffect(() => {
    if (!room) return
    if (room.phase !== 'round_intro') {
      hasFiredDrawRef.current = false
      return
    }
    if (!iDrive) return
    if (hasFiredDrawRef.current) return
    hasFiredDrawRef.current = true
    drawColorfulCard(room.id).catch(e => {
      hasFiredDrawRef.current = false
      setError(readError(e))
    })
  }, [room, me, iDrive])

  // ── AI orchestrator: the host fires picks for any AI seats ────────────
  // Triggered when phase flips to 'picking'. Uses a ref to ensure we only
  // kick off once per (room, round).
  const lastPickedRoundRef = useRef<number>(-1)
  useEffect(() => {
    if (!room || !iDrive) return
    if (room.phase !== 'picking') return
    if (lastPickedRoundRef.current === room.current_round) return

    const aiSeats = players.filter(p => p.is_ai && !p.skipped)
    if (aiSeats.length === 0) return

    const subject = room.pending_subject ?? ''
    const available = room.available_colors as ColorId[]

    lastPickedRoundRef.current = room.current_round
    setAiBusy(true)

    ;(async () => {
      try {
        const tasks = aiSeats.map(async (seat) => {
          // Skip AIs that have already picked this round (defensive).
          const already = picks.find(pk =>
            pk.player_id === seat.id && pk.round_index === room.current_round && pk.color_id,
          )
          if (already) return

          const usedByThisAi = new Set(
            picks.filter(pk => pk.player_id === seat.id && pk.color_id).map(pk => pk.color_id!),
          )
          const hand = available.filter(c => !usedByThisAi.has(c))

          const persona = await personaFor(seat.ai_player_id!).catch(() => null)
          const res = await requestAiColorPick({
            personaName: seat.display_name,
            personaDescription: persona?.persona ?? '',
            subject,
            availableColorIds: hand,
            model: persona?.model,
          })
          await submitColorfulPickAsAi(seat.id, res.colorId)
        })

        await Promise.all(tasks)
      } catch (e) {
        console.error('[colorful] AI pick batch failed:', e)
      } finally {
        setAiBusy(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.phase, room?.current_round, iDrive, players.length])

  // ── AI picker auto-generates a subject and locks in the card ─────────
  // Triggered when phase='picker_draw' and the current picker is an AI seat.
  const lastSubjectRoundRef = useRef<number>(-1)
  useEffect(() => {
    if (!room || !iDrive) return
    if (room.phase !== 'picker_draw') return
    if (room.pending_category_id === null) return
    if (lastSubjectRoundRef.current === room.current_round) return
    if (!players.length) return

    const picker = players[room.picker_index % players.length]
    if (!picker) return
    // AI pickers auto-pick. A SKIPPED human picker is handled the same
    // way — any driver generates a fallback subject — so a dropped
    // picker can never strand the room in picker_draw until expiry.
    if (!picker.is_ai && !picker.skipped) return
    lastSubjectRoundRef.current = room.current_round

    const card = CATEGORIES.find(c => c.id === room.pending_category_id)
    const prompt = card?.prompts[room.pending_slot ?? 0] ?? ''

    ;(async () => {
      try {
        const persona = picker.is_ai && picker.ai_player_id
          ? await personaFor(picker.ai_player_id).catch(() => null)
          : null
        const subject = await requestAiSubject({
          personaName: picker.display_name,
          categoryPrompt: prompt,
          model: persona?.model,
        })
        await confirmColorfulCard(room.id, subject)
      } catch (e) {
        console.error('[colorful] AI subject generation failed:', e)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.phase, room?.current_round, room?.pending_category_id, iDrive, players])

  // ── Auto-reveal when every seat has picked (any present human) ─────────
  useEffect(() => {
    if (!room || !iDrive) return
    if (room.phase !== 'picking') return
    const activeSeats = players.filter(p => !p.skipped)
    if (activeSeats.length === 0) return
    const needed = activeSeats.length
    const pickedThisRound = picks.filter(
      pk => pk.round_index === room.current_round && pk.color_id
        && activeSeats.some(p => p.id === pk.player_id),
    ).length
    if (pickedThisRound >= needed) {
      revealColorfulRound(roomId).catch(e => setError(readError(e)))
    }
  }, [room?.phase, room?.current_round, picks, players, iDrive, roomId])

  // ── Loading / error gates ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader title={t('Colorful')} subtitle={t('Loading room…')} onBack={onExit} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader title={t('Colorful')} onBack={onExit} />
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <p className="text-sm text-brand-danger">{error ?? t('Room not found')}</p>
        </div>
      </div>
    )
  }

  // ── Phase router ──────────────────────────────────────────────────────
  if (room.phase === 'lobby') {
    return (
      <LobbyView
        room={room} players={players} isHost={isHost} onExit={onExit}
        onError={setError}
        onLeave={async () => { await leaveColorfulRoom(roomId); onExit() }}
      />
    )
  }

  if (room.phase === 'setup') {
    return (
      <SetupView
        room={room} players={players} isHost={isHost} onExit={onExit}
        onError={setError}
      />
    )
  }

  // Picker is whoever sits at this round's `picker_index`. There's
  // exactly ONE in-game screen — "Your pick". The picker (only) sees
  // a different view because they're typing the subject; everyone
  // else sits on the same Your-Pick screen with a "Waiting for X to
  // pick a subject" message until the subject is confirmed, then the
  // colour grid appears. No round_intro / draw-card flash screens.
  const picker = players[room.picker_index % Math.max(1, players.length)]
  const pickerIsMe = Boolean(mySeat && picker && mySeat.id === picker.id)

  const inGameplay =
    room.phase === 'round_intro' ||
    room.phase === 'picker_draw' ||
    room.phase === 'picking'

  if (inGameplay) {
    if (room.phase === 'picker_draw' && pickerIsMe) {
      return (
        <PickerDrawView
          room={room} players={players} picks={picks}
          isHost={isHost} mySeat={mySeat} onExit={onExit}
          onError={setError}
        />
      )
    }
    return (
      <PickingView
        room={room} players={players} picks={picks} mySeat={mySeat}
        isHost={isHost} aiBusy={aiBusy}
        onExit={onExit}
        onError={setError}
      />
    )
  }

  // Per the physical rules, there's no between-round reveal. The DB no
  // longer emits 'round_reveal' — if a stale server version still does,
  // we just fall through to the intro of the next round.

  if (room.phase === 'results') {
    return (
      <ResultsView
        room={room} players={players} picks={picks}
        isHost={isHost}
        onExit={onExit}
      />
    )
  }

  return null
}

// ── Sub-views ──────────────────────────────────────────────────────────

type ViewProps<T> = {
  room: ColorfulRoomRow
  players: ColorfulPlayerRow[]
  isHost: boolean
  onExit: () => void
  onError: (e: string) => void
} & T

function LobbyView({
  room, players, isHost, onExit, onError, onLeave,
}: ViewProps<{ onLeave: () => Promise<void> }>) {
  const t = useT()
  const { profile } = useAuth()
  const [personas, setPersonas] = useState<AiPlayerRow[]>([])
  const [addingAi, setAddingAi] = useState(false)
  const [starting, setStarting] = useState(false)

  // My current seat (if any) so the user can rename themselves in-room.
  const mySeat = profile?.id ? players.find(p => p.profile_id === profile.id) ?? null : null
  const [nameDraft, setNameDraft] = useState<string>(mySeat?.display_name ?? '')
  const [renaming, setRenaming] = useState(false)
  useEffect(() => {
    if (mySeat?.display_name) setNameDraft(mySeat.display_name)
  }, [mySeat?.display_name])

  async function saveNameIfChanged() {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === mySeat?.display_name) return
    setRenaming(true)
    try { await renameColorfulSeat(room.id, trimmed) }
    catch (e) { onError(readError(e)) }
    finally { setRenaming(false) }
  }

  useEffect(() => {
    fetchColorfulAiPlayers(6).then(setPersonas).catch(() => {})
  }, [])

  // Filter out personas that are already seated.
  const seatedAiIds = new Set(players.filter(p => p.is_ai).map(p => p.ai_player_id!))
  const availablePersonas = personas.filter(p => !seatedAiIds.has(p.id))
  const canStart = players.length >= 2 && players.length <= 6

  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader
        title={t('Room {code}', { code: room.code })}
        subtitle={t('{n} / 6 players · waiting to start', { n: players.length })}
        onBack={onExit}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
        {/* TV URL banner — shown to the host in every room. The TV
            surface is just a big-screen *view* of this same room; any
            laptop or TV pointed at the URL will display the game. The
            URL is a real <a> so the host can long-press → Copy or
            tap-and-hold → Share to AirPlay/AirDrop the link to the
            display device without retyping a code. */}
        {isHost && (
          <div className="card-lg p-4 bg-linear-to-br from-brand-text to-gray-800 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/60 mb-1">
              {t('📺 Bigger visuals? Open this on a TV / laptop')}
            </p>
            <a
              href={`https://games.jordandraper.com/tv/colorful/${room.code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-base font-semibold break-all underline decoration-white/40 hover:decoration-white"
            >
              games.jordandraper.com/tv/colorful/{room.code}
            </a>
            <p className="text-xs text-white/60 mt-2">{t('Tap to open · long-press to copy/share. Optional: your phone already runs the game. This is a read-only big-screen view.')}</p>
          </div>
        )}

        {/* Your name — in-room rename. Updates both profile (sticky
            across rooms) and the seat snapshot (UI updates now). */}
        {mySeat && (
          <div>
            <label htmlFor="colorful-room-name" className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="colorful-room-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={saveNameIfChanged}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              disabled={renaming}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-brand-primary focus:ring-2
                         focus:ring-brand-primary/20 transition-all disabled:opacity-60"
            />
          </div>
        )}

        {/* Room code banner */}
        <div className="bg-game-colorful-light rounded-3xl shadow-card-lg p-5
                        border-2 border-game-colorful-dark text-center">
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
            {t('Players join at')}
          </p>
          <a
            href={`https://games.jordandraper.com/join/${room.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm text-brand-text underline decoration-brand-text/30 hover:decoration-brand-text"
          >
            games.jordandraper.com/join/{room.code}
          </a>
          <p className="font-display text-5xl font-black text-brand-text tracking-[0.2em] mt-3">
            {room.code}
          </p>
          <div className="flex justify-center mt-4">
            <RoomQR code={room.code} />
          </div>
        </div>

        {/* Seated players */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Players')}
          </p>
          <div className="space-y-2">
            {players.map(p => (
              <div key={p.id} className="card p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-elevated flex
                                items-center justify-center text-xl shrink-0">
                  {p.avatar_emoji ?? (p.display_name[0] ?? '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-text truncate">{p.display_name}</p>
                  <p className="text-xs text-brand-muted">
                    {p.is_host ? 'Host' : p.is_ai ? 'AI' : 'Player'}
                  </p>
                </div>
                {isHost && !p.is_host && (
                  <button
                    onClick={async () => {
                      try { await removeColorfulPlayer(p.id) }
                      catch (e) { onError(readError(e)) }
                    }}
                    className="text-brand-muted hover:text-brand-danger px-2 py-1 text-xs"
                    aria-label={t('Remove')}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {players.length < 6 && (
              <div className="card p-3 text-center text-xs text-brand-muted">
                {t('Waiting for more players…')}
              </div>
            )}
          </div>
        </div>

        {/* Host: add AI */}
        {isHost && availablePersonas.length > 0 && players.length < 6 && (
          <div>
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t('Add an AI opponent')}
            </p>
            <div className="space-y-2">
              {availablePersonas.map(p => (
                <button
                  key={p.id}
                  disabled={addingAi}
                  onClick={async () => {
                    setAddingAi(true)
                    try { await addColorfulAi(room.id, p.id) }
                    catch (e) { onError(readError(e)) }
                    finally { setAddingAi(false) }
                  }}
                  className="card w-full p-3 flex items-center gap-3 text-left
                             active:bg-brand-elevated transition-colors disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-full bg-brand-elevated flex
                                  items-center justify-center text-xl shrink-0">
                    {p.avatar_emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-text truncate">{p.name}</p>
                    <p className="text-xs text-brand-muted truncate">{t(p.persona)}</p>
                  </div>
                  <span className="text-xs text-brand-muted shrink-0">{t('+ Add')}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="floating-actions space-y-3">
        {isHost ? (
          <button
            disabled={!canStart || starting}
            onClick={async () => {
              setStarting(true)
              try { await startColorful(room.id) }
              catch (e) { onError(readError(e)); setStarting(false) }
            }}
            className="btn-primary disabled:opacity-40"
          >
            {players.length < 2 ? t('Need at least 2 players') : starting ? 'Starting…' : t('▶ Start game')}
          </button>
        ) : (
          <div className="card p-3 text-center text-sm text-brand-muted">
            {t('Waiting for the host to start the game…')}
          </div>
        )}
        <button onClick={onLeave} className="btn-ghost w-full text-center">{t('Leave room')}</button>
      </div>
    </div>
  )
}

function SetupView({ room, onExit, onError }: ViewProps<object>) {
  const t = useT()
  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader title={t('Colorful')} subtitle={t('Setup')} onBack={onExit} />
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-8 gap-6">
        <div className="text-center">
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
            {t("Tonight you won't be needing")}
          </p>
          <h2 className="font-display text-2xl font-bold text-brand-text">
            {t('These five colors')}
          </h2>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {room.discarded_colors.map(id => (
            <div key={id} className="relative opacity-70">
              <ColorCardImage color={id as ColorId} size="sm" />
              <svg viewBox="0 0 100 100"
                   className="absolute inset-0 w-full h-full pointer-events-none"
                   preserveAspectRatio="none" aria-hidden>
                <line x1="12" y1="12" x2="88" y2="88"
                      stroke="rgba(0,0,0,0.6)" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
          ))}
        </div>
      </div>
      <div className="floating-actions">
        {/* Any player can deal — the RPC is idempotent so simultaneous
            taps are harmless. The game never waits on the host. */}
        <button
          className="btn-primary"
          onClick={async () => {
            try { await beginColorfulRound(room.id) } catch (e) { onError(readError(e)) }
          }}
        >
          {t('Ready, deal round 1')}
        </button>
      </div>
    </div>
  )
}

// RoundIntroView and the matching "show the card" screen were deleted
// in favour of a single in-game view: PickingView. There is no flash
// frame for non-pickers — they sit on PickingView the whole time, with
// a "waiting for X to pick a subject" stub until the subject lands.
// The host's auto-draw effect at the top of ColorfulRoomScreen
// collapses round_intro → picker_draw the moment phase flips.

/**
 * Renders the player's hand as a fanned spread of cards (Hearthstone /
 * Solitaire arc). Each card pivots around a shared point well below
 * the row, so they fan up and outward symmetrically.
 */
function HandFan({ colors, label }: { colors: ColorId[]; label: string }) {
  const t = useT()
  if (colors.length === 0) return null
  const n = colors.length
  // Tighter angle for fewer cards, wider for the full hand.
  const fanDeg = Math.min(72, n * 6)
  const step = n > 1 ? fanDeg / (n - 1) : 0
  const startDeg = -fanDeg / 2
  return (
    <div className="mt-4 space-y-2">
      <p className="text-xs font-medium text-brand-muted uppercase tracking-wide text-center">
        {label} · {n === 1 ? t('1 card') : t('{n} cards', { n })}
      </p>
      <div className="relative h-32 flex items-end justify-center select-none">
        <div className="relative h-full" style={{ width: '220px' }}>
          {colors.map((c, i) => (
            <div
              key={c}
              className="absolute bottom-0 left-1/2"
              style={{
                transformOrigin: '50% 240px',
                transform: `translateX(-50%) rotate(${startDeg + step * i}deg)`,
                zIndex: i,
              }}
            >
              <ColorCardImage color={c} size="sm" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function PickerDrawView({
  room, players, picks, isHost, mySeat, onExit, onError,
}: ViewProps<{ picks: ColorfulPickRow[]; mySeat: ColorfulPlayerRow | null }>) {
  const t = useT()
  const card = CATEGORIES.find(c => c.id === room.pending_category_id)
  const slot = room.pending_slot ?? 0
  const categoryPrompt = card?.prompts[slot] ?? ''
  const picker = players[room.picker_index % Math.max(1, players.length)]
  const pickerIsMe = Boolean(mySeat && picker && mySeat.id === picker.id)
  const pickerIsAi = !!picker?.is_ai

  const [subjectDraft, setSubjectDraft] = useState('')
  const [subjectError, setSubjectError] = useState<string | null>(null)
  const [locking, setLocking] = useState(false)

  // Reset the draft when a new round starts / a redraw happens.
  useEffect(() => {
    setSubjectDraft('')
    setSubjectError(null)
  }, [room.current_round, room.pending_category_id])

  const trimmed = subjectDraft.trim()
  const hasColorWord = trimmed && COLOR_WORDS.some(
    w => new RegExp(`\\b${w}\\b`, 'i').test(trimmed),
  )
  // Reject the bare category — "food" isn't a valid subject of category "food".
  // Also strip a leading article so "a food" still trips if prompt is "food".
  const normSubject = trimmed.toLowerCase().replace(/^(a |an |the )/, '')
  const normPrompt = categoryPrompt.toLowerCase().replace(/^(a |an |the )/, '')
  const matchesCategory = trimmed.length > 0 && normSubject === normPrompt
  const canLock = trimmed.length >= 2 && !hasColorWord && !matchesCategory && !locking

  async function lockIn() {
    if (!canLock) return
    setLocking(true); setSubjectError(null)
    try { await confirmColorfulCard(room.id, trimmed) }
    catch (e) { onError(readError(e)); setLocking(false) }
  }

  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader
        title={t('Round {n}', { n: room.current_round + 1 })}
        subtitle={t("{name}'s turn", { name: picker?.display_name ?? '…' })}
        onBack={onExit}
      />
      <div className="flex-1 flex flex-col px-5 py-6 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
        {card && (
          <div className="flex flex-col items-center gap-3">
            {/* Real category card art, rotated landscape so the
                prompt rows read left-to-right. The slot indicator
                below tells the picker which of the four prompts is
                in play this round. */}
            <CategoryCardImage
              src={card.cardFront}
              alt={t('{theme} category card', { theme: card.theme })}
              size="xl"
            />
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-brand-muted">
                {t('Round prompt #{n}', { n: slot === 3 ? '4-5' : slot + 1 })}
              </p>
              <p className="font-display text-xl font-bold text-brand-text uppercase tracking-wide">
                {categoryPrompt}
              </p>
            </div>
          </div>
        )}

        {pickerIsMe ? (
          <div className="bg-game-colorful-light rounded-2xl shadow-card p-4
                          border-2 border-game-colorful-dark">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-2">
              {t('Your category ·')} <span className="text-brand-text">{categoryPrompt}</span>
            </p>
            <p className="text-sm text-brand-muted mb-3">{t('Pick something specific in this category for everyone to colour. No color words (e.g. "bluejay").')}</p>
            <input
              type="text"
              value={subjectDraft}
              onChange={e => setSubjectDraft(e.target.value.slice(0, 60))}
              placeholder={t('e.g. {example}', { example: exampleFor(categoryPrompt) })}
              autoFocus
              maxLength={60}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-brand-primary focus:ring-2
                         focus:ring-brand-primary/20 transition-all"
            />
            {hasColorWord && (
              <p className="text-xs text-brand-danger mt-2">
                {t('No color words allowed. Try something else.')}
              </p>
            )}
            {matchesCategory && !hasColorWord && (
              <p className="text-xs text-brand-danger mt-2">{t("That's the category. Be more specific (e.g. a single example).")}</p>
            )}
            {subjectError && !hasColorWord && !matchesCategory && (
              <p className="text-xs text-brand-danger mt-2">{subjectError}</p>
            )}
            {/* Picker's hand — colours still in play. The picker should
                pick a subject they can actually colour with one of these. */}
            {mySeat && (
              <HandFan
                colors={(room.available_colors as ColorId[]).filter(c =>
                  !picks.some(pk => pk.player_id === mySeat.id
                    && pk.round_index < room.current_round
                    && pk.color_id === c))}
                label={t('Your hand')}
              />
            )}
          </div>
        ) : pickerIsAi ? (
          <div className="bg-game-colorful-light rounded-2xl shadow-card p-4
                          border-2 border-game-colorful-dark text-center">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
              {t('{name} is thinking…', { name: picker?.display_name ?? '…' })}
            </p>
            <p className="font-display text-xl font-bold text-brand-text">
              {t('Category: {prompt}', { prompt: categoryPrompt })}
            </p>
          </div>
        ) : (
          <div className="bg-game-colorful-light rounded-2xl shadow-card p-4
                          border-2 border-game-colorful-dark text-center">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
              {t('Waiting for {name}', { name: picker?.display_name ?? '…' })}
            </p>
            <p className="font-display text-xl font-bold text-brand-text">
              {t('Category: {prompt}', { prompt: categoryPrompt })}
            </p>
            <p className="text-sm text-brand-muted mt-2">
              {t("They're picking a specific subject in this category.")}
            </p>
            {/* Escape hatch for an absent picker — skipping them flips
                this seat to AI handling and the round auto-advances. */}
            {picker && !pickerIsMe && !pickerIsAi && (
              <div className="mt-3 flex justify-center">
                <ColorfulSkipButton
                  playerId={picker.id}
                  name={picker.display_name}
                  onError={onError}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="floating-actions space-y-3">
        {pickerIsMe ? (
          <>
            {isHost && !room.redrew && room.deck.length > 0 && (
              <button
                className="btn-outline"
                onClick={async () => {
                  try { await redrawColorfulCard(room.id) } catch (e) { onError(readError(e)) }
                }}
              >
                🔄 Redraw card (one chance)
              </button>
            )}
            <button
              className="btn-primary disabled:opacity-40"
              disabled={!canLock}
              onClick={lockIn}
            >
              {locking ? t('Locking in…')
                : !trimmed ? t('Type a subject')
                : hasColorWord ? t('No color words')
                : matchesCategory ? t('Be more specific')
                : '✅ Lock it in'}
            </button>
          </>
        ) : (
          <div className="card p-3 text-center text-sm text-brand-muted">
            {pickerIsAi
              ? t('{name} is picking a subject…', { name: picker?.display_name ?? '…' })
              : t('Waiting for {name} to lock it in…', { name: picker?.display_name ?? '…' })}
          </div>
        )}
      </div>
    </div>
  )
}

// Small set of per-prompt placeholders — just nudges so the input has a
// concrete example. Not validated against, purely hint text.
function exampleFor(prompt: string): string {
  const p = prompt.toLowerCase()
  if (p.includes('feeling') || p.includes('emotion')) return 'regret'
  if (p.includes('animal')) return 'elephant'
  if (p.includes('folktale') || p.includes('myth')) return 'Cinderella'
  if (p.includes('food') || p.includes('drink')) return 'curry'
  if (p.includes('movie') || p.includes('film')) return 'Star Wars'
  if (p.includes('song') || p.includes('music')) return 'Bohemian Rhapsody'
  if (p.includes('country')) return 'Japan'
  if (p.includes('city')) return 'Tokyo'
  if (p.includes('sport')) return 'surfing'
  if (p.includes('season')) return 'autumn'
  return 'something specific'
}

function PickingView({
  room, players, picks, mySeat, aiBusy, onExit, onError,
}: ViewProps<{ picks: ColorfulPickRow[]; mySeat: ColorfulPlayerRow | null; aiBusy: boolean }>) {
  const t = useT()
  const myPick = picks.find(
    pk => pk.player_id === mySeat?.id && pk.round_index === room.current_round,
  )
  const myPicked = myPick?.color_id as ColorId | null

  const usedByMe = new Set(
    picks.filter(pk => pk.player_id === mySeat?.id && pk.color_id).map(pk => pk.color_id!),
  )
  const remaining = (room.available_colors as ColorId[]).filter(c => !usedByMe.has(c))

  const activeSeats = players.filter(p => !p.skipped)
  const pickedCount = activeSeats.filter(p => picks.find(
    pk => pk.player_id === p.id && pk.round_index === room.current_round && pk.color_id,
  )).length

  // Picker context — surfaces who chose the subject and from which
  // category prompt, so the rest of the table has the same framing
  // they had at the physical table.
  const picker = players[room.picker_index % Math.max(1, players.length)]
  const card = CATEGORIES.find(c => c.id === room.pending_category_id)
  const slot = room.pending_slot ?? 0
  const categoryPrompt = card?.prompts[slot] ?? ''
  const subject = room.pending_subject ?? ''

  // We're in "your pick" the whole time the picker is choosing — no
  // separate "they're drawing" / "they're picking a subject" screens.
  // When there's no subject yet, show a waiting state in the same
  // shell instead of the colour grid.
  const hasSubject = Boolean(subject)

  // Subtitle shows round + the category card's theme (e.g. "Round 2 ·
  // Objects"). The actual picked subject is rendered in the body so
  // the table sees both: the category they're working in, and the
  // specific thing the picker chose.
  const themeLabel = card?.theme ?? '…'

  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader
        title={mySeat ? t('Your pick') : 'Picking'}
        subtitle={t('Round {n} · {theme}', { n: room.current_round + 1, theme: themeLabel })}
        onBack={onExit}
      />
      <div className="px-5 py-5 space-y-4 glass-panes">
        <div className="bg-game-colorful-light rounded-2xl shadow-card p-4
                        border-2 border-game-colorful-dark space-y-2">
          {hasSubject ? (
            <>
              <p className="text-sm text-brand-text leading-relaxed">
                <strong>{picker?.display_name ?? '…'}</strong>'s category was
                {' '}<strong className="uppercase">{categoryPrompt || '…'}</strong>{t(', and the subject is')} <strong>{subject}</strong>.
              </p>
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest pt-1 border-t border-game-colorful-dark/30">
                {mySeat ? t('Choose the color that best matches') : 'Subject'}
              </p>
              <p className="font-display text-2xl font-bold text-brand-text">
                {subject}
              </p>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="text-4xl mb-2">{picker?.avatar_emoji ?? '🎨'}</div>
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest">
                {t('Waiting for')}
              </p>
              <p className="font-display text-2xl font-bold text-brand-text">
                {picker?.display_name ?? '…'}
              </p>
              <p className="text-sm text-brand-muted mt-1">
                {categoryPrompt
                  ? t('to pick a subject for "{prompt}".', { prompt: categoryPrompt })
                  : t('to pick a subject.')}
              </p>
            </div>
          )}
        </div>

        {/* Colour grid + roster only render once the subject is in.
            Until then the user is staring at the waiting card above. */}
        {hasSubject && (mySeat ? (
          <div className="grid grid-cols-3 gap-3">
            {remaining.map(id => {
              const selected = myPicked === id
              return (
                <button
                  key={id}
                  disabled={!!myPicked}
                  onClick={async () => {
                    try { await submitColorfulPick(room.id, id) }
                    catch (e) { onError(readError(e)) }
                  }}
                  aria-label={COLORS_BY_ID[id].name}
                  className={`active:scale-95 transition-transform
                              ${selected ? 'scale-95' : ''}
                              disabled:opacity-60`}
                >
                  <ColorCardImage color={id} selected={selected} size="lg" />
                </button>
              )
            })}
          </div>
        ) : (
          <div className="card p-4 text-center text-sm text-brand-muted">
            {t("You're hosting. Players will pick on their own phones.")}
          </div>
        ))}

        {hasSubject && <div className="card p-4 space-y-2">
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
            {aiBusy
              ? t('{n} of {total} picked · AIs thinking…', { n: pickedCount, total: activeSeats.length })
              : t('{n} of {total} picked', { n: pickedCount, total: activeSeats.length })}
          </p>
          {activeSeats.map(p => {
            const theirPick = picks.find(
              pk => pk.player_id === p.id && pk.round_index === room.current_round && pk.color_id,
            )
            const stuck = !theirPick && !p.is_ai
            return (
              <div key={p.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-elevated flex items-center
                                justify-center text-base">
                  {p.avatar_emoji ?? (p.display_name[0] ?? '?').toUpperCase()}
                </div>
                <span className="flex-1 text-sm text-brand-text truncate">{p.display_name}</span>
                {/* Any player can skip a human who hasn't picked (the host
                    may be the one who dropped). Soft-skip excludes them
                    from the gate so the round advances. */}
                {stuck && p.profile_id !== mySeat?.profile_id && (
                  <ColorfulSkipButton playerId={p.id} name={p.display_name} onError={onError} />
                )}
                <span className="text-xs text-brand-muted">
                  {theirPick ? '✓' : p.is_ai && aiBusy ? '…' : 'waiting'}
                </span>
              </div>
            )
          })}
        </div>}
      </div>
    </div>
  )
}

function ColorfulSkipButton({
  playerId, name, onError,
}: { playerId: string; name: string; onError: (msg: string) => void }) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  async function onClick() {
    if (busy) return
    if (!confirm(t("Skip {name}? They'll sit out the rest of the game and the round will advance.", { name }))) return
    setBusy(true)
    try { await kickColorfulPlayer(playerId) }
    catch (e) { onError(readError(e)) }
    finally { setBusy(false) }
  }
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="text-xs font-semibold text-brand-danger underline decoration-brand-danger/40
                 hover:decoration-brand-danger disabled:opacity-50"
    >
      {busy ? 'Skipping…' : 'Skip'}
    </button>
  )
}

// RoundRevealView removed — the physical rules keep every round's picks
// face-down until the finale. The server's `reveal_colorful_round` RPC
// now advances directly to the next round (or to 'results' if the match
// is over) without exposing anyone's color to the rest of the table.

function ResultsView({
  room, players, picks, isHost, onExit,
}: {
  room: ColorfulRoomRow; players: ColorfulPlayerRow[]; picks: ColorfulPickRow[]
  isHost: boolean; onExit: () => void
}) {
  const t = useT()
  // Score + compute per-round groupings up-front. Same logic as before
  // (majority scoring with tied majorities worth 0, co-op win on all-
  // unanimous rounds).
  const scored = useMemo(() => {
    type RoundPicks = Record<string, string>
    const rounds: Array<{ round: number; picks: RoundPicks }> = []
    for (let r = 0; r < room.max_rounds; r++) {
      const forRound: RoundPicks = {}
      for (const pk of picks) {
        if (pk.round_index === r && pk.color_id) forRound[pk.player_id] = pk.color_id
      }
      rounds.push({ round: r, picks: forRound })
    }

    const points: Record<string, number> = {}
    for (const p of players) points[p.id] = 0
    let coopWin = rounds.length > 0

    const perRound = rounds.map(({ picks: rp }) => {
      const groups: Record<string, string[]> = {}
      for (const [pid, color] of Object.entries(rp)) (groups[color] ??= []).push(pid)
      const sizes = Object.values(groups).map(g => g.length)
      const total = Object.values(rp).length
      const max = sizes.length ? Math.max(...sizes) : 0
      const winning = Object.entries(groups).filter(([, g]) => g.length === max && max > 0).map(([c]) => c)
      const unanimous = total > 0 && max === total
      if (!unanimous) coopWin = false
      // V2 rulebook: ties for the majority all score 1 point each.
      // "If there is a tie in color majorities, all tied players
      // receive 1 point." (Game End, pg.1)
      for (const winningColor of winning) {
        for (const pid of groups[winningColor]) points[pid] = (points[pid] ?? 0) + 1
      }
      return {
        // For UI highlighting, we still mark a single-majority round
        // distinctly (vs a multi-way tie where multiple colours score).
        winningColor: winning.length === 1 ? winning[0] : null,
        groups,
        unanimous,
        tiedWinners: winning.length > 1 ? winning : null,
      }
    })

    const top = Math.max(0, ...Object.values(points))
    const winners = Object.entries(points).filter(([, p]) => p === top && top > 0).map(([pid]) => pid)
    return { points, perRound, coopWin, winners }
  }, [picks, players, room.max_rounds])

  const sortedPlayers = [...players].sort((a, b) => (scored.points[b.id] ?? 0) - (scored.points[a.id] ?? 0))

  // Stepped reveal, paged LOCALLY: every player walks the rounds at
  // their own speed — nobody waits on the host (owner request,
  // 2026-08-15). 0..max_rounds-1 = reveal round N, max_rounds = final
  // scoreboard. The host's taps additionally mirror to reveal_index so
  // a TV open on this room still follows the host's pace.
  const [step, setStep] = useState(0)
  const isFinalScoreStep = step >= room.max_rounds

  function advance() {
    if (isHost) {
      // Best-effort TV sync — a failure here must never block the
      // host's own local paging.
      void setColorfulRevealIndex(room.id, step + 1).catch(() => {})
    }
    setStep(s => s + 1)
  }

  // Per-round metadata — category theme + subject — from the colorful_picks rows.
  function metaForRound(roundIndex: number): { theme: string; subject: string; categoryId: string | null } {
    const any = picks.find(pk => pk.round_index === roundIndex)
    const cat = any ? CATEGORIES.find(c => c.id === any.category_id) : null
    return {
      theme: cat?.theme ?? '…',
      subject: any?.subject ?? '…',
      categoryId: any?.category_id ?? null,
    }
  }

  // ── Per-round reveal step ──────────────────────────────────────────────
  if (!isFinalScoreStep) {
    const meta = metaForRound(step)
    const round = scored.perRound[step]
    const sortedGroups = round
      ? Object.entries(round.groups).sort((a, b) => b[1].length - a[1].length)
      : []
    const isLastRound = step + 1 >= room.max_rounds

    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader
          title={t('Reveal · Round {n} of {total}', { n: step + 1, total: room.max_rounds })}
          subtitle={meta.theme}
          onBack={onExit}
        />
        <div className="px-5 py-5 space-y-5 overflow-y-auto pb-32 glass-panes">
          <div className="bg-game-colorful-light rounded-3xl shadow-card-lg p-5 text-center
                          border-2 border-game-colorful-dark">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
              {t('The subject was')}
            </p>
            <p className="font-display text-3xl font-bold text-brand-text leading-tight">
              {meta.subject}
            </p>
            {round?.unanimous && (
              <p className="text-xs text-iet-green font-semibold mt-2">{t('✨ Unanimous!')}</p>
            )}
          </div>

          <div className="space-y-2">
            {sortedGroups.map(([color, playerIds]) => {
              const names = playerIds
                .map(id => players.find(p => p.id === id)?.display_name)
                .filter(Boolean) as string[]
              const isMajority = color === round?.winningColor
              return (
                <div key={color}
                     className={`card p-4 flex items-center gap-3
                                 ${isMajority ? 'border-2 border-brand-gold/40 bg-brand-gold/5' : ''}`}>
                  <ColorCardImage color={color as ColorId} size="sm" className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-text">
                      {COLORS_BY_ID[color as ColorId].name}
                    </p>
                    <p className="text-xs text-brand-muted truncate">{names.join(', ')}</p>
                  </div>
                  <span className="font-display font-bold text-brand-text text-lg shrink-0">
                    ×{playerIds.length}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
        <div className="floating-actions">
          <button onClick={advance} className="btn-primary">
            {isLastRound ? t('See final scores →') : t('Round {n} →', { n: step + 2 })}
          </button>
        </div>
      </div>
    )
  }

  // ── Final scoreboard ──────────────────────────────────────────────────
  // Winner up top, then the full story: every round's subject and who
  // picked what color — the results of what happened, not just a score.
  const recapRounds: RecapRound[] = scored.perRound
    .map((r, i) => {
      const meta = metaForRound(i)
      return {
        index: i,
        theme: meta.theme,
        subject: meta.subject,
        unanimous: r.unanimous,
        groups: Object.entries(r.groups)
          .sort((a, b) => b[1].length - a[1].length)
          .map(([color, ids]) => ({
            color: color as ColorId,
            names: ids.map(id => players.find(p => p.id === id)?.display_name).filter(Boolean) as string[],
            isMajority: color === r.winningColor,
          })),
      }
    })
    .filter(r => r.groups.length > 0)

  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader title={t('Colorful')} subtitle={t('Final results')} onBack={onExit} />
      <div className="px-5 py-5 space-y-5 overflow-y-auto pb-28 glass-panes">
        {scored.coopWin ? (
          <div className="relative bg-game-colorful-light rounded-3xl shadow-card-lg p-6 text-center
                          border-2 border-game-colorful-dark animate-scale-in champion-glow">
            <div className="relative text-6xl mb-2 animate-bounce-soft drop-shadow-xs">🌈</div>
            <p className="relative text-xs font-semibold text-game-colorful uppercase tracking-widest mb-2">
              {t('Co-op Victory')}
            </p>
            <h2 className="relative font-display text-2xl font-bold text-brand-text">
              {t('You all saw the same colors.')}
            </h2>
          </div>
        ) : scored.winners.length === 0 ? (
          <div className="card-lg p-6 text-center bg-brand-elevated">
            <div className="text-5xl mb-2">🎭</div>
            <h2 className="font-display text-2xl font-bold text-brand-text">
              {t('No winners this game')}
            </h2>
            <p className="text-sm text-brand-muted mt-2">
              {t('Every round was too divided to score. Try again. Looser categories help.')}
            </p>
          </div>
        ) : (
          <div className="relative card-lg p-6 text-center bg-linear-to-br
                          from-brand-gold/20 to-iet-orange/10
                          border-2 border-brand-gold/40 animate-scale-in champion-glow">
            <div className="relative text-6xl mb-2 animate-bounce-soft drop-shadow-xs">🏆</div>
            <p className="relative text-xs font-semibold text-brand-gold uppercase tracking-widest mb-2">
              {scored.winners.length > 1 ? t('Shared victory') : 'Winner'}
            </p>
            <h2 className="relative font-display text-2xl font-bold text-brand-text leading-tight">
              {scored.winners.map(id => players.find(p => p.id === id)?.display_name).filter(Boolean).join(' & ')}
            </h2>
            <p className="relative text-brand-primary font-bold text-lg mt-2">
              {scored.points[scored.winners[0]] === 1
                ? t('1 point')
                : t('{n} points', { n: scored.points[scored.winners[0]] })}
            </p>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-brand-text mb-3">{t('Leaderboard')}</h3>
          <div className="space-y-2">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="card p-4 flex items-center gap-3">
                <RankMedal rank={i + 1} size="md" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-text truncate">{p.display_name}</p>
                </div>
                <div className="flex flex-col items-end shrink-0">
                  <span className="font-display font-bold text-brand-text">
                    {scored.points[p.id] ?? 0}
                  </span>
                  <span className="text-[10px] text-brand-muted">points</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AllRoundsRecap rounds={recapRounds} />
      </div>

      <div className="floating-actions">
        <button onClick={onExit} className="btn-primary">{t('Back to games')}</button>
      </div>
    </div>
  )
}

// ── Small helpers ──────────────────────────────────────────────────────

async function personaFor(aiPlayerId: string): Promise<AiPlayerRow | null> {
  const { data, error } = await supabase
    .from('ai_players').select('*').eq('id', aiPlayerId).maybeSingle()
  if (error) throw error
  return (data as AiPlayerRow | null) ?? null
}
